import React, {
  RefObject,
  useEffect,
} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import clsx from 'clsx';
import FormTextField from 'views/EMR/components/FillableForm/components/FormTextField';
import ButtonGroup from '@mui/material/ButtonGroup';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';

interface SignaturePoint {
  x: number;
  y: number;
  time?: number;
  pressure?: number;
}

type DrawingData = SignaturePoint[][];

type SignatureBoxProps = {
  mode: 'draw' | 'type';
  onModeChange: (mode: 'draw' | 'type') => void;
  canvasRef: RefObject<SignatureCanvas | null>;
  boxRef: RefObject<HTMLDivElement | null>;
  canvasWidth: number;
  canvasHeight: number;
  typedValue: string;
  onDraw: (data: string) => void;
  onType: (value: string) => void;
  onClear: () => void;
  setInteracted: (value: boolean) => void;
  isDisabled: boolean;
  placeholder: string;
  inputId: string;
  drawingData?: DrawingData;
  onSaveDrawingData?: (data: DrawingData) => void;
};

type SignatureBoxFooterProps = {
  onClear: () => void;
};

function SignatureBoxFooter({ onClear }: SignatureBoxFooterProps) {
  return (
    <>
      <div className="border-b-2 border-gray-300 mt-4" />
      <div className="flex justify-between items-center mt-3">
        <span className="text-sm text-gray-500 truncate">
          Sign above
        </span>
        <button
          type="button"
          className="text-sm text-gray-500 underline flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onClear}
        >
          Clear
        </button>
      </div>
    </>
  );
}

export default function SignatureBox({
  mode,
  onModeChange,
  canvasRef,
  boxRef,
  canvasWidth,
  canvasHeight,
  typedValue,
  onDraw,
  onType,
  onClear,
  setInteracted,
  isDisabled,
  placeholder,
  inputId,
  drawingData,
  onSaveDrawingData,
}: SignatureBoxProps) {
  const isDrawing = mode === 'draw';
  const isTyping = mode === 'type';

  useEffect(() => {
    if (isDrawing && drawingData && canvasRef?.current) {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!isNil(canvas)) {
          (canvas.fromData as (data: DrawingData) => void)(drawingData);
        }
      }, 50);
    }
  }, [isDrawing, drawingData, canvasRef]);

  return (
    <div className="flex flex-col gap-4">
      <ButtonGroup variant="outlined" color="inherit" aria-label="Signature mode">
        <Button
          variant="outlined"
          className={clsx(
            'flex gap-2 !h-8 !min-w-0 !px-3 !normal-case !border-gray-300',
            {
              '!bg-blue-500 !text-white !border-blue-500': isTyping,
              'text-gray-600 bg-transparent': !isTyping,
            },
          )}
          onClick={() => { onModeChange('type'); }}
        >
          Type
        </Button>
        <Button
          variant="outlined"
          className={clsx(
            'flex gap-2 !h-8 !min-w-0 !px-3 !normal-case !border-gray-300',
            {
              '!bg-blue-500 !text-white !border-blue-500': isDrawing,
              'text-gray-600 bg-transparent': !isDrawing,
            },
          )}
          onClick={() => { onModeChange('draw'); }}
        >
          Draw
        </Button>
      </ButtonGroup>

      {isDrawing && (
        <div className="max-w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div ref={boxRef} className="w-full max-w-[420px]">
              <SignatureCanvas
                ref={canvasRef}
                penColor="#345D7E"
                canvasProps={{
                  width: canvasWidth,
                  height: canvasHeight,
                  className: clsx(
                    'w-full border-0 block touch-none',
                    { 'opacity-50 pointer-events-none': isDisabled },
                  ),
                }}
                onEnd={() => {
                  setInteracted(true);
                  const data = canvasRef.current?.toDataURL?.('image/png') ?? '';
                  onDraw(data);
                  const drawingStrokes = canvasRef.current?.toData?.();
                  onSaveDrawingData?.(drawingStrokes as DrawingData);
                }}
              />
            </div>
            <SignatureBoxFooter onClear={onClear} />
          </div>
        </div>
      )}

      {isTyping && (
        <div className="max-w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <FormTextField
              value={typedValue}
              id={inputId}
              fullWidth
              placeholder={placeholder}
              onChange={(e) => {
                setInteracted(true);
                onType(e?.target?.value ?? '');
              }}
            />
            <div className="bg-gray-50 rounded mt-4 p-6 min-h-24 flex items-center justify-center">
              {!isEmptyString(typedValue) && (
                <p className="text-4xl dancing-script-regular text-[#345D7E]">
                  {typedValue}
                </p>
              )}
            </div>
            <SignatureBoxFooter onClear={onClear} />
          </div>
        </div>
      )}
    </div>
  );
}
